// client/src/stores/toast-store.ts — the toast queue · UI-003 (04 §5.9).
//
// Zustand is right for this and it is worth saying why, because `ui-store.ts` states the rule
// the other way round. A toast is not server data (the server has no opinion about whether a
// message is still on screen), and the URL must not restore it (re-opening a link would
// re-announce „Plata a fost înregistrată" for a payment registered yesterday). It is exactly
// what is left after the litmus test — transient viewport state — so it belongs in a store.
//
// It is its own store rather than a slice of `ui-store.ts` because the two have different
// lifetimes: everything in `ui-store` is a preference that persists for the session, and
// everything here is gone within eight seconds.
//
// The TIMERS are not here. Auto-dismiss pauses on hover and focus (§5.9), which only the
// rendered element knows about, so each `<Toast>` owns its own clock and calls `dismiss`.

import { create } from 'zustand';

export type ToastVariant = 'success' | 'info' | 'warning' | 'error';

export interface ToastAction {
  /** Already translated by the caller — the store never holds a key (D-321 in reverse). */
  label: string;
  onAction: () => void;
}

export interface Toast {
  id: string;
  variant: ToastVariant;
  /** Already translated by the caller; §5.9 wants the object reference in it. */
  message: string;
  /**
   * Optional undo (§5.9). NOT for fiscal confirmations — those are audited actions and are
   * reversed only through an explicit flow, never by a five-second window.
   */
  action?: ToastAction;
}

export type ToastInput = Omit<Toast, 'id'>;

/** §5.9: 5 s, and 8 s for errors — an error carries a correlation id worth reading. */
export const TOAST_DURATION_MS: Record<ToastVariant, number> = {
  success: 5_000,
  info: 5_000,
  warning: 5_000,
  error: 8_000,
};

interface ToastState {
  toasts: readonly Toast[];
  push: (toast: ToastInput) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  push: (toast) => {
    const id = crypto.randomUUID();
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));
    return id;
  },

  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }));
  },

  clear: () => {
    set({ toasts: [] });
  },
}));

/**
 * Raising a toast from outside React — a mutation's `onSuccess`, a router error handler.
 * Reads the store imperatively, so it never needs a component to be rendering.
 */
export const toast = {
  success: (message: string, action?: ToastAction) => push('success', message, action),
  info: (message: string, action?: ToastAction) => push('info', message, action),
  warning: (message: string, action?: ToastAction) => push('warning', message, action),
  error: (message: string, action?: ToastAction) => push('error', message, action),
};

function push(variant: ToastVariant, message: string, action?: ToastAction): string {
  // `exactOptionalPropertyTypes` — an absent action is an absent KEY, not an `undefined` value.
  return useToastStore
    .getState()
    .push(action ? { variant, message, action } : { variant, message });
}
